import { useMemo, useRef, useState, useEffect } from 'react';
import { UnifiedTable } from '../../../../components/UnifiedTable';
import { useApi } from '../../../../hooks/useApi';
import logger from "../../../../utils/logger";
import ToolHelpButton from '../../../../components/ToolHelpButton';
const initialParams = {
  mode: 'article_cluster',
  period_from: '',
  period_to: '',
  growth_mode: 'manual',
  growth_coef: '1.15',
  paid_storage_threshold_rub: '150',
  localize_share: '20',
  quant_saturation: '50',
  min_places_for_distribution: '1'
};
const COLUMNS = [{
  key: 'rowNum',
  header: '№',
  width: 56,
  type: 'number',
  sticky: true,
  sortable: false
}, {
  key: 'article',
  header: 'Артикул',
  width: 180,
  type: 'text',
  sticky: true
}, {
  key: 'cluster_name',
  header: 'Кластер',
  width: 180,
  type: 'text'
}, {
  key: 'warehouse_name',
  header: 'Склад',
  width: 220,
  type: 'text'
}, {
  key: 'stock_units',
  header: 'Остаток',
  width: 120,
  type: 'number'
}, {
  key: 'forecast_supply_units',
  header: 'Прогноз к поставке',
  width: 180,
  type: 'number'
}, {
  key: 'orders_per_day',
  header: 'Заказов/день',
  width: 140,
  type: 'number'
}, {
  key: 'turnover_days',
  header: 'Остаток, дн',
  width: 120,
  type: 'number'
}, {
  key: 'paid_units',
  header: 'Платно',
  width: 110,
  type: 'number'
}, {
  key: 'fee_rub_day',
  header: 'Начислено/день, ₽',
  width: 160,
  type: 'number'
}, {
  key: 'paid_storage_total',
  header: 'Платное хранение за период, ₽',
  width: 220,
  type: 'number'
}, {
  key: 'comment',
  header: 'Комментарий',
  width: 340,
  type: 'text'
}];
const formatDate = value => {
  if (!value) return '';
  const parts = value.split('-');
  if (parts.length !== 3) return value;
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
};
const calcPaidStorageTotal = (stockUnits, ordersPerDay, feePerDay) => {
  if (!stockUnits || !ordersPerDay || !feePerDay) return 0;
  const stock = Number(stockUnits);
  const rate = Number(ordersPerDay);
  const fee = Number(feePerDay);
  if (!stock || !rate || !fee) return 0;
  const perUnitFee = fee;
  const days = Math.floor((stock - 1) / rate) + 1;
  const sumStock = days * (2 * stock - (days - 1) * rate) / 2;
  return sumStock * perUnitFee;
};
const toNumber = (v, d = 0) => {
  if (v === null || v === undefined || v === '') return d;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : d;
};
const downloadTextFile = (filename, content, type = 'text/plain;charset=utf-8') => {
  const blob = new Blob([content], {
    type
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};
function OzonPaidStorage({
  accountId,
  accountName
}) {
  const {
    request,
    authFetch,
    API_BASE_URL,
    showStatus
  } = useApi();
  const [file, setFile] = useState(null);
  const [reportId, setReportId] = useState(null);
  const [dateRange, setDateRange] = useState({
    from: '',
    to: ''
  });
  const [lastReportName, setLastReportName] = useState('');
  const [calcId, setCalcId] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useState(initialParams);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [paidFilter, setPaidFilter] = useState('all');
  const [syncBusy, setSyncBusy] = useState(false);
  const [articleFilter, setArticleFilter] = useState('');
  const [clusterFilter, setClusterFilter] = useState('');
  const [warehouseFilter, setWarehouseFilter] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [boxRules, setBoxRules] = useState([]);
  const [clusterDistribution, setClusterDistribution] = useState([]);
  const [boxSheetOpen, setBoxSheetOpen] = useState(false);
  const [clusterSheetOpen, setClusterSheetOpen] = useState(false);
  const boxImportRef = useRef(null);
  const clusterImportRef = useRef(null);
  useEffect(() => {
    if (!accountId) return;
    let mounted = true;
    const loadLastReport = async () => {
      try {
        const data = await request(`/api/v1/reports/last?account_id=${accountId}`, {});
        if (!mounted) return;
        if (!data) throw new Error('last report is absent');
        setReportId(data.report_id);
        setDateRange({
          from: data.date_from,
          to: data.date_to
        });
        setLastReportName(data.filename || '');
        setParams(prev => ({
          ...prev,
          period_from: data.date_from,
          period_to: data.date_to
        }));
      } catch (error) {
        if (!mounted) return;
        setReportId(null);
        setDateRange({
          from: '',
          to: ''
        });
        setLastReportName('');
      }
    };
    loadLastReport();
    return () => {
      mounted = false;
    };
  }, [accountId, request]);
  const handleUpload = async () => {
    if (!file) {
      showStatus('error', 'Выберите файл отчёта (XLSX/CSV).');
      return;
    }
    setBusy(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('report_name', file.name || 'ozon_storage_report');
      if (accountId) {
        formData.append('account_id', accountId);
      }
      const data = await authFetch(`${API_BASE_URL}/api/v1/reports/upload`, {
        method: 'POST',
        body: formData
      });
      setReportId(data.report_id);
      setDateRange({
        from: data.date_from,
        to: data.date_to
      });
      setLastReportName(file.name || '');
      setParams(prev => ({
        ...prev,
        period_from: data.date_from,
        period_to: data.date_to
      }));
      setCalcId(null);
      setRecommendations([]);
      setTotal(0);
      setPage(1);
      showStatus('success', 'Отчёт загружен.');
    } catch (error) {
      logger.error(error);
      showStatus('error', 'Ошибка загрузки отчёта.');
    } finally {
      setBusy(false);
    }
  };
  const loadRecommendations = async (calcIdValue, pageValue = 1, pageSizeValue = pageSize, filterValue = paidFilter, sortKeyValue = sortKey, sortDirValue = sortDir, articleValue = articleFilter, clusterValue = clusterFilter, warehouseValue = warehouseFilter) => {
    if (!reportId || !calcIdValue) return;
    setBusy(true);
    try {
      const safePageSize = pageSizeValue === 'all' ? total > 0 ? total : 1000 : pageSizeValue;
      const query = new URLSearchParams({
        page: String(pageValue),
        page_size: String(safePageSize),
        paid_filter: filterValue
      });
      if (articleValue) query.append('article', articleValue);
      if (clusterValue) query.append('cluster', clusterValue);
      if (warehouseValue) query.append('warehouse', warehouseValue);
      if (sortKeyValue) query.append('sort_key', sortKeyValue);
      if (sortDirValue) query.append('sort_dir', sortDirValue);
      const data = await request(`/api/v1/reports/${reportId}/recommendations/${calcIdValue}?${query.toString()}`, {});
      setRecommendations(data.items || []);
      setTotal(data.total || 0);
      if (pageSizeValue === 'all') {
        setPageSize(data.total || 1000);
      }
    } catch (error) {
      logger.error(error);
      showStatus('error', 'Не удалось загрузить рекомендации.');
    } finally {
      setBusy(false);
    }
  };
  const handleSyncClusters = async () => {
    setSyncBusy(true);
    try {
      await request('/api/v1/ozon/clusters/sync', {
        method: 'POST',
        body: JSON.stringify({
          account_name: accountName || 'BES'
        })
      });
      showStatus('success', 'Кластеры обновлены.');
    } catch (error) {
      logger.error(error);
      showStatus('error', 'Не удалось обновить кластеры.');
    } finally {
      setSyncBusy(false);
    }
  };
  const parseCsvSimple = text => {
    const lines = text.split(/\r?\n/).map(x => x.trim()).filter(Boolean);
    if (!lines.length) return [];
    const head = lines[0].split(',').map(x => x.trim());
    return lines.slice(1).map(line => {
      const cols = line.split(',').map(x => x.trim());
      const row = {};
      head.forEach((h, i) => {
        row[h] = cols[i] ?? '';
      });
      return row;
    });
  };
  const loadArticlesFromAssortment = async () => {
    if (!accountId) return;
    try {
      let offset = 0;
      const limit = 500;
      let totalRows = 0;
      const all = [];
      do {
        const q = new URLSearchParams({
          limit: String(limit),
          offset: String(offset),
          search: '',
          sort_by: '',
          sort_dir: 'asc'
        });
        const data = await request(`/api/v1/ozon/${accountId}/assortment?${q.toString()}`, {});
        const rows = data.rows || [];
        totalRows = data.total || rows.length;
        all.push(...rows);
        offset += rows.length;
        if (!rows.length) break;
      } while (offset < totalRows);
      const uniq = new Map();
      for (const row of all) {
        const article = String(row.offer_id || '').trim();
        if (!article) continue;
        if (!uniq.has(article)) uniq.set(article, {
          article,
          multiple: 1,
          available: ''
        });
      }
      const prev = new Map(boxRules.map(x => [x.article, x]));
      const merged = Array.from(uniq.values()).map(x => ({
        ...x,
        ...(prev.get(x.article) || {})
      }));
      setBoxRules(merged.sort((a, b) => a.article.localeCompare(b.article)));
      showStatus('success', `Загружено артикулов: ${merged.length}`);
    } catch (error) {
      logger.error(error);
      showStatus('error', 'Не удалось загрузить ассортимент.');
    }
  };
  const loadClustersForDistribution = async () => {
    try {
      const data = await request('/api/v1/ozon/clusters/list', {});
      const items = (data.items || []).map(x => ({
        cluster_name: x.cluster_name || x.cluster_id || '',
        ordered_units: '',
        share: ''
      }));
      const prev = new Map(clusterDistribution.map(x => [x.cluster_name, x]));
      const merged = items.map(x => ({
        ...x,
        ...(prev.get(x.cluster_name) || {})
      }));
      setClusterDistribution(merged);
      showStatus('success', `Загружено кластеров: ${merged.length}`);
    } catch (error) {
      logger.error(error);
      showStatus('error', 'Не удалось загрузить кластеры.');
    }
  };
  const exportBoxRulesCsv = () => {
    const header = 'article,multiple,available';
    const lines = boxRules.map(r => `${r.article || ''},${r.multiple || ''},${r.available ?? ''}`);
    downloadTextFile('box_rules.csv', [header, ...lines].join('\n'), 'text/csv;charset=utf-8');
  };
  const exportClustersCsv = () => {
    const header = 'cluster_name,ordered_units,share';
    const lines = clusterDistribution.map(r => `${r.cluster_name || ''},${r.ordered_units ?? ''},${r.share ?? ''}`);
    downloadTextFile('cluster_distribution.csv', [header, ...lines].join('\n'), 'text/csv;charset=utf-8');
  };
  const importBoxRules = async fileObj => {
    const text = await fileObj.text();
    const parsed = fileObj.name.toLowerCase().endsWith('.json') ? JSON.parse(text) : parseCsvSimple(text);
    const rows = (Array.isArray(parsed) ? parsed : []).map(r => ({
      article: String(r.article || '').trim(),
      multiple: Math.max(1, Math.floor(toNumber(r.multiple, 1))),
      available: r.available === '' || r.available == null ? '' : Math.max(0, Math.floor(toNumber(r.available, 0)))
    })).filter(x => x.article);
    setBoxRules(rows);
    showStatus('success', `Импортировано правил: ${rows.length}`);
  };
  const importClusterDistribution = async fileObj => {
    const text = await fileObj.text();
    const parsed = fileObj.name.toLowerCase().endsWith('.json') ? JSON.parse(text) : parseCsvSimple(text);
    const rows = (Array.isArray(parsed) ? parsed : []).map(r => ({
      cluster_name: String(r.cluster_name || '').trim(),
      ordered_units: r.ordered_units ?? '',
      share: r.share ?? ''
    })).filter(x => x.cluster_name);
    setClusterDistribution(rows);
    showStatus('success', `Импортировано кластеров: ${rows.length}`);
  };
  const handleCalculate = async () => {
    if (!reportId) {
      showStatus('error', 'Сначала загрузите отчёт.');
      return;
    }
    setBusy(true);
    try {
      await request(`/api/v1/reports/${reportId}/build-fact`, {
        method: 'POST'
      });
      const payload = {
        mode: params.mode,
        period_from: params.period_from || null,
        period_to: params.period_to || null,
        min_obs_days_for_rate: 7,
        growth_mode: params.growth_mode || 'manual',
        growth_coef: params.growth_mode === 'manual' && params.growth_coef ? Number(params.growth_coef) : null,
        paid_storage_threshold_rub: params.paid_storage_threshold_rub ? Number(params.paid_storage_threshold_rub) : null,
        include_paid_storage: true,
        localize_share: params.localize_share ? Number(params.localize_share) : 0,
        quant_saturation: params.quant_saturation ? Number(params.quant_saturation) : 50,
        min_places_for_distribution: params.min_places_for_distribution ? Number(params.min_places_for_distribution) : 0,
        box_rules: boxRules.filter(x => x.article).map(x => ({
          article: x.article,
          multiple: Math.max(1, Math.floor(toNumber(x.multiple, 1))),
          available: x.available === '' ? null : Math.max(0, Math.floor(toNumber(x.available, 0)))
        })),
        cluster_distribution: clusterDistribution.filter(x => x.cluster_name).map(x => ({
          cluster_name: x.cluster_name,
          ordered_units: x.ordered_units === '' ? null : toNumber(x.ordered_units, 0),
          share: x.share === '' ? null : toNumber(x.share, 0)
        }))
      };
      const data = await request(`/api/v1/reports/${reportId}/recommendations`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      setCalcId(data.calc_id);
      showStatus('success', 'Рекомендации рассчитаны.');
      await loadRecommendations(data.calc_id, 1, pageSize, paidFilter);
      setPage(1);
    } catch (error) {
      logger.error(error);
      showStatus('error', 'Ошибка расчёта.');
    } finally {
      setBusy(false);
    }
  };
  const summary = useMemo(() => {
    if (!recommendations.length) return null;
    const totalPaid = recommendations.reduce((acc, item) => acc + (item.paid_units || 0), 0);
    const totalFee = recommendations.reduce((acc, item) => acc + Number(item.fee_rub_day || 0), 0);
    return {
      totalPaid,
      totalFee
    };
  }, [recommendations]);
  const tableData = useMemo(() => recommendations.map((row, index) => {
    const orders = row.orders_per_day != null ? Number(row.orders_per_day) : 0;
    const stock = row.stock_units != null ? Number(row.stock_units) : 0;
    const turnoverDays = orders > 0 ? stock / orders : null;
    const paidTotal = calcPaidStorageTotal(stock, orders, row.fee_rub_day);
    return {
      ...row,
      rowNum: (page - 1) * pageSize + index + 1,
      cluster_name: row.cluster_name || row.cluster_id || '-',
      warehouse_name: row.warehouse_name || row.warehouse_id || '-',
      forecast_supply_units: row.forecast_supply_units != null ? Number(row.forecast_supply_units) : 0,
      orders_per_day: orders ? orders.toFixed(2) : '-',
      turnover_days: turnoverDays != null ? turnoverDays.toFixed(1) : '-',
      fee_rub_day: row.fee_rub_day != null ? Number(row.fee_rub_day).toFixed(2) : '-',
      paid_storage_total: paidTotal ? paidTotal.toFixed(2) : '-',
      comment: row.comment || '-'
    };
  }), [recommendations, page, pageSize]);
  return <div className="panel__section">
            <div className="panel__head">
                <div>
                    <h3>Платное хранение OZON<ToolHelpButton toolKey="ozon_paid_storage" /></h3>
                    <p className="muted">Загрузите отчет: FBO - Платное хранение - Скачать отчет (по товарам) и выберите месяц.</p>
                </div>
                <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '8px'
      }}>
                    <button type="button" className="ghost" onClick={handleSyncClusters} disabled={syncBusy}>
            
                        Обновить кластеры
                    </button>
                    {summary && <div className="muted">
                            Платных остатков: {summary.totalPaid},
                            Начислено/день: {summary.totalFee.toFixed(2)} ₽
                        </div>}
                </div>
            </div>

            <div className="panel__grid">
                <label>
                    Отчёт (XLSX/CSV)
                    <div className="ozon-storage__file">
                        <input id="ozon-storage-file" type="file" accept=".xlsx,.xls,.csv" onChange={event => setFile(event.target.files?.[0] || null)} disabled={busy} />
            
                        <label htmlFor="ozon-storage-file" className="ozon-storage__file-btn">
                            Выбрать файл
                        </label>
                        <span className="muted">{file ? file.name : lastReportName || 'Файл не выбран'}</span>
                    </div>
                </label>
                <div style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: '10px'
      }}>
                    <button type="button" className="primary" onClick={handleUpload} disabled={!file || busy}>
                        Загрузить отчёт
                    </button>
                </div>
            </div>

            {dateRange.from && dateRange.to && <div className="muted" style={{
      marginTop: '8px'
    }}>
                    Диапазон отчёта: {formatDate(dateRange.from)} - {formatDate(dateRange.to)}
                </div>}

            <div className="panel__section" style={{
      marginTop: '16px'
    }}>
                <h4>Параметры расчёта</h4>
                <div className="panel__grid">
                    <label className="label-regular">
                        Тип счёта заказов в день
                        <select value={params.mode} onChange={event => {
            const value = event.target.value;
            setParams(prev => ({
              ...prev,
              mode: value
            }));
            if (reportId && !busy) {
              setTimeout(() => handleCalculate(), 0);
            }
          }}>
              
                            <option value="article_cluster">артикул-кластер</option>
                            <option value="article_warehouse">артикул-склад</option>
                        </select>
                    </label>
                    <label>
                        Режим коэффициента роста
                        <select value={params.growth_mode} onChange={event => setParams(prev => ({
            ...prev,
            growth_mode: event.target.value
          }))}>
              
                            <option value="manual">Ручной</option>
                            <option value="article_weekly">Поартикульный</option>
                        </select>
                    </label>
                    <label>
                        Коэф. роста продаж
                        <input type="number" step="0.01" value={params.growth_coef} onChange={event => setParams(prev => ({
            ...prev,
            growth_coef: event.target.value
          }))} disabled={params.growth_mode !== 'manual'} />
            
                    </label>
                    <label>
                        Доля потребности в локализацию, %
                        <input type="number" step="0.01" min="0" value={params.localize_share} onChange={event => setParams(prev => ({
            ...prev,
            localize_share: event.target.value
          }))} />
            
                    </label>
                    <label>
                        Процент насыщения кванта, %
                        <input type="number" step="0.01" min="0" max="100" value={params.quant_saturation} onChange={event => setParams(prev => ({
            ...prev,
            quant_saturation: event.target.value
          }))} />
            
                    </label>
                    <label>
                        Минимум мест для локализации
                        <input type="number" min="0" value={params.min_places_for_distribution} onChange={event => setParams(prev => ({
            ...prev,
            min_places_for_distribution: event.target.value
          }))} />
            
                    </label>
                    <label>
                        Порог платного хранения, ₽
                        <input type="number" min="0" value={params.paid_storage_threshold_rub} onChange={event => setParams(prev => ({
            ...prev,
            paid_storage_threshold_rub: event.target.value
          }))} />
            
                    </label>
                    <label>
                        Период с
                        <input type="date" value={params.period_from} onChange={event => setParams(prev => ({
            ...prev,
            period_from: event.target.value
          }))} />
            
                    </label>
                    <label>
                        Период по
                        <input type="date" value={params.period_to} onChange={event => setParams(prev => ({
            ...prev,
            period_to: event.target.value
          }))} />
            
                    </label>
                </div>

                <div className="panel__section" style={{
        marginTop: 16
      }}>
                    <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
                        <h4 style={{
            margin: 0
          }}>Лист: Кратность коробов</h4>
                        <button type="button" className="ghost" onClick={() => setBoxSheetOpen(v => !v)}>
                            {boxSheetOpen ? 'Свернуть' : 'Развернуть'}
                        </button>
                    </div>
                    {boxSheetOpen && <>
                            <div style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            margin: '8px 0'
          }}>
                                <button type="button" className="ghost" onClick={loadArticlesFromAssortment}>Загрузить артикулы из ассортимента</button>
                                <button type="button" className="ghost" onClick={() => setBoxRules(p => [...p, {
              article: '',
              multiple: 1,
              available: ''
            }])}>Добавить строку</button>
                                <button type="button" className="ghost" onClick={exportBoxRulesCsv}>Выгрузить CSV</button>
                                <button type="button" className="ghost" onClick={() => boxImportRef.current?.click()}>Импорт CSV/JSON</button>
                                <input ref={boxImportRef} type="file" accept=".csv,.json" style={{
              display: 'none'
            }} onChange={e => e.target.files?.[0] && importBoxRules(e.target.files[0])} />
                            </div>
                            <div style={{
            overflowX: 'auto'
          }}>
                                <table style={{
              width: '100%',
              borderCollapse: 'collapse'
            }}>
                                    <thead>
                                        <tr>
                                            <th style={{
                    borderBottom: '1px solid #e6e9ef',
                    padding: '8px 6px',
                    textAlign: 'left'
                  }}>Артикул</th>
                                            <th style={{
                    borderBottom: '1px solid #e6e9ef',
                    padding: '8px 6px',
                    textAlign: 'left'
                  }}>Кратность</th>
                                            <th style={{
                    borderBottom: '1px solid #e6e9ef',
                    padding: '8px 6px',
                    textAlign: 'left'
                  }}>Остаток</th>
                                            <th style={{
                    borderBottom: '1px solid #e6e9ef',
                    padding: '8px 6px'
                  }} />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {boxRules.map((row, idx) => <tr key={`${row.article}-${idx}`}>
                                                <td style={{
                    borderBottom: '1px solid #f0f2f5',
                    padding: '8px 6px'
                  }}><input value={row.article} onChange={e => setBoxRules(p => p.map((x, i) => i === idx ? {
                      ...x,
                      article: e.target.value
                    } : x))} /></td>
                                                <td style={{
                    borderBottom: '1px solid #f0f2f5',
                    padding: '8px 6px'
                  }}><input type="number" min="1" value={row.multiple} onChange={e => setBoxRules(p => p.map((x, i) => i === idx ? {
                      ...x,
                      multiple: e.target.value
                    } : x))} /></td>
                                                <td style={{
                    borderBottom: '1px solid #f0f2f5',
                    padding: '8px 6px'
                  }}><input type="number" min="0" value={row.available} onChange={e => setBoxRules(p => p.map((x, i) => i === idx ? {
                      ...x,
                      available: e.target.value
                    } : x))} placeholder="пусто = без лимита" /></td>
                                                <td style={{
                    borderBottom: '1px solid #f0f2f5',
                    padding: '8px 6px'
                  }}><button type="button" className="ghost" onClick={() => setBoxRules(p => p.filter((_, i) => i !== idx))}>Удалить</button></td>
                                            </tr>)}
                                    </tbody>
                                </table>
                            </div>
                        </>}
                </div>

                <div className="panel__section" style={{
        marginTop: 16
      }}>
                    <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
                        <h4 style={{
            margin: 0
          }}>Лист: Процент распределения по кластерам</h4>
                        <button type="button" className="ghost" onClick={() => setClusterSheetOpen(v => !v)}>
                            {clusterSheetOpen ? 'Свернуть' : 'Развернуть'}
                        </button>
                    </div>
                    {clusterSheetOpen && <>
                            <div style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            margin: '8px 0'
          }}>
                                <button type="button" className="ghost" onClick={loadClustersForDistribution}>Загрузить существующие кластеры</button>
                                <button type="button" className="ghost" onClick={() => setClusterDistribution(p => [...p, {
              cluster_name: '',
              ordered_units: '',
              share: ''
            }])}>Добавить строку</button>
                                <button type="button" className="ghost" onClick={exportClustersCsv}>Выгрузить CSV</button>
                                <button type="button" className="ghost" onClick={() => clusterImportRef.current?.click()}>Импорт CSV/JSON</button>
                                <input ref={clusterImportRef} type="file" accept=".csv,.json" style={{
              display: 'none'
            }} onChange={e => e.target.files?.[0] && importClusterDistribution(e.target.files[0])} />
                            </div>
                            <div style={{
            overflowX: 'auto'
          }}>
                                <table style={{
              width: '100%',
              borderCollapse: 'collapse'
            }}>
                                    <thead>
                                        <tr>
                                            <th style={{
                    borderBottom: '1px solid #e6e9ef',
                    padding: '8px 6px',
                    textAlign: 'left'
                  }}>Кластер</th>
                                            <th style={{
                    borderBottom: '1px solid #e6e9ef',
                    padding: '8px 6px',
                    textAlign: 'left'
                  }}>Заказано по всем кластерам</th>
                                            <th style={{
                    borderBottom: '1px solid #e6e9ef',
                    padding: '8px 6px',
                    textAlign: 'left'
                  }}>Доля %</th>
                                            <th style={{
                    borderBottom: '1px solid #e6e9ef',
                    padding: '8px 6px'
                  }} />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {clusterDistribution.map((row, idx) => <tr key={`${row.cluster_name}-${idx}`}>
                                                <td style={{
                    borderBottom: '1px solid #f0f2f5',
                    padding: '8px 6px'
                  }}><input value={row.cluster_name} onChange={e => setClusterDistribution(p => p.map((x, i) => i === idx ? {
                      ...x,
                      cluster_name: e.target.value
                    } : x))} /></td>
                                                <td style={{
                    borderBottom: '1px solid #f0f2f5',
                    padding: '8px 6px'
                  }}><input type="number" min="0" value={row.ordered_units} onChange={e => setClusterDistribution(p => p.map((x, i) => i === idx ? {
                      ...x,
                      ordered_units: e.target.value
                    } : x))} /></td>
                                                <td style={{
                    borderBottom: '1px solid #f0f2f5',
                    padding: '8px 6px'
                  }}><input type="number" min="0" step="0.01" value={row.share} onChange={e => setClusterDistribution(p => p.map((x, i) => i === idx ? {
                      ...x,
                      share: e.target.value
                    } : x))} /></td>
                                                <td style={{
                    borderBottom: '1px solid #f0f2f5',
                    padding: '8px 6px'
                  }}><button type="button" className="ghost" onClick={() => setClusterDistribution(p => p.filter((_, i) => i !== idx))}>Удалить</button></td>
                                            </tr>)}
                                    </tbody>
                                </table>
                            </div>
                        </>}
                </div>

                <div style={{
        marginTop: '12px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px',
        alignItems: 'center'
      }}>
                    <button type="button" className="primary" onClick={handleCalculate} disabled={busy || !reportId}>
                        Рассчитать
                    </button>
                    <label className="ozon-storage__accent" style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
                        Платное хранение
                        <select value={paidFilter} onChange={event => {
            const value = event.target.value;
            setPaidFilter(value);
            if (calcId) {
              loadRecommendations(calcId, 1, pageSize, value, sortKey, sortDir);
              setPage(1);
            }
          }}>
              
                            <option value="all">все</option>
                            <option value="paid">только платное</option>
                            <option value="free">без платного</option>
                        </select>
                    </label>
                    {calcId && <button type="button" className="ghost" onClick={() => loadRecommendations(calcId, page, pageSize, paidFilter, sortKey, sortDir)} disabled={busy}>
            
                            Обновить таблицу
                        </button>}
                </div>
            </div>

            <div className="panel__section" style={{
      marginTop: '16px'
    }}>
                <h4>Фильтры</h4>
                <div className="panel__grid">
                    <label>
                        Артикул
                        <input type="text" value={articleFilter} onChange={event => setArticleFilter(event.target.value)} onBlur={() => calcId && loadRecommendations(calcId, 1, pageSize, paidFilter, sortKey, sortDir)} />
            
                    </label>
                    <label>
                        Кластер
                        <input type="text" value={clusterFilter} onChange={event => setClusterFilter(event.target.value)} onBlur={() => calcId && loadRecommendations(calcId, 1, pageSize, paidFilter, sortKey, sortDir)} />
            
                    </label>
                    <label>
                        Склад
                        <input type="text" value={warehouseFilter} onChange={event => setWarehouseFilter(event.target.value)} onBlur={() => calcId && loadRecommendations(calcId, 1, pageSize, paidFilter, sortKey, sortDir)} />
            
                    </label>
                </div>
            </div>

            <UnifiedTable tableId="ozon-paid-storage" columns={COLUMNS} data={tableData} totalRows={total} page={page} pageSize={pageSize} onPageChange={nextPage => {
      setPage(nextPage);
      if (calcId) loadRecommendations(calcId, nextPage, pageSize, paidFilter, sortKey, sortDir);
    }} onPageSizeChange={nextSize => {
      const normalized = nextSize === 'all' ? total > 0 ? total : 1000 : nextSize;
      setPageSize(normalized);
      setPage(1);
      if (calcId) loadRecommendations(calcId, 1, normalized, paidFilter, sortKey, sortDir);
    }} sortKey={sortKey} sortDir={sortDir} onSortChange={(key, dir) => {
      setSortKey(key);
      setSortDir(dir);
      if (calcId) loadRecommendations(calcId, 1, pageSize, paidFilter, key, dir);
    }} serverSort={true} onExportCsv={true} onCopyTable={true} loading={busy} getRowKey={(row, index) => `${row.article}-${row.warehouse_name}-${row.cluster_name}-${index}`} />
      
        </div>;
}
export default OzonPaidStorage;
